import React, { useState, useEffect } from 'react';
import { AppProvider, useApp } from './context/AppContext';
import { ThemeProvider } from './context/ThemeContext';
import { DocumentUpload } from './components/DocumentUpload';
import { TemplateLibrary } from './components/TemplateLibrary';
import { Preview } from './components/Preview';
import { Export } from './components/Export';
import { Login } from './components/Login';
import { DarkModeToggle } from './components/DarkModeToggle';
import { AlgemeneVoorwaarde } from './components/AlgemeneVoorwaarde';
import { AlgemeneVoorwaardeToggle } from './components/AlgemeneVoorwaardeToggle';

function AppContent() {
  const { state, dispatch } = useApp();
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    // Check if user is already authenticated in session storage
    const authStatus = sessionStorage.getItem('fwa_authenticated');
    if (authStatus === 'true') {
      setIsAuthenticated(true);
    }
  }, []);

  const handleLogin = (success: boolean) => {
    if (success) {
      setIsAuthenticated(true);
      sessionStorage.setItem('fwa_authenticated', 'true');
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    sessionStorage.removeItem('fwa_authenticated');
  };

  if (!isAuthenticated) {
    return <Login onLogin={handleLogin} />;
  }

  return (
    <div className="min-h-screen bg-gray-100 dark:bg-black transition-colors">
      <header className="bg-white dark:bg-black shadow-sm border-b border-gray-200 dark:border-blue-600 transition-colors">
        <div className="max-w-[1920px] mx-auto px-4 sm:px-6 lg:px-12 py-4">
          <div className="flex justify-between items-center">
            <DarkModeToggle />
            <div className="text-center">
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white">FWa</h1>
              <h2 className="text-lg text-gray-600 dark:text-white mt-1">PDF Converter</h2>
            </div>
            <div className="flex space-x-2">
              <AlgemeneVoorwaarde />
              <button
                onClick={handleLogout}
                className="text-sm text-gray-600 dark:text-white hover:text-gray-800 dark:hover:text-blue-400 px-3 py-1 rounded border border-gray-300 dark:border-blue-600 hover:border-gray-400 dark:hover:border-blue-400 transition-colors"
              >
                Uitloggen
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-[1920px] mx-auto px-4 sm:px-6 lg:px-12 py-8">
        {state.error && (
          <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
            <div className="flex justify-between items-center">
              <span>{state.error}</span>
              <button
                onClick={() => dispatch({ type: 'CLEAR_ERROR' })}
                className="text-red-500 hover:text-red-700"
              >
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div className="space-y-8">
            <DocumentUpload />
            <TemplateLibrary />
            <AlgemeneVoorwaardeToggle />
            <Export />
          </div>
          
          <div>
            <Preview />
          </div>
        </div>

        {state.isLoading && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white p-6 rounded-lg shadow-xl">
              <div className="flex items-center space-x-3">
                <svg className="animate-spin h-5 w-5 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <span className="text-gray-700">Bezig met verwerken...</span>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

function App() {
  return (
    <ThemeProvider>
      <AppProvider>
        <AppContent />
      </AppProvider>
    </ThemeProvider>
  );
}

export default App;
